/**
 * Date validation utilities for tourist visa applications
 */

export const validateDateInput = (dateString) => {
  if (!dateString) {
    return { isValid: false, error: 'Please select a visa issue date' };
  }

  const date = new Date(dateString);
  const today = new Date();
  const maxDate = new Date();
  maxDate.setFullYear(today.getFullYear() + 1); // Allow up to 1 year in future

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return { isValid: false, error: 'Please enter a valid date' };
  }

  // Check if date is not too far in the future (max 1 year)
  if (date > maxDate) {
    return { 
      isValid: false, 
      error: 'Visa issue date cannot be more than 1 year in the future' 
    };
  }

  // Check if date is not too far in the past (max 5 years for tourist visas)
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 5);
  
  if (date < minDate) {
    return { 
      isValid: false, 
      error: 'Visa issue date cannot be more than 5 years in the past' 
    };
  }

  return { isValid: true, error: null };
};

export const validateVisaDuration = (duration) => {
  if (!duration || duration === '') {
    return { isValid: false, error: 'Please enter a visa duration' };
  }

  const numericDuration = parseInt(duration, 10);
  
  if (isNaN(numericDuration)) {
    return { isValid: false, error: 'Please enter a valid number' };
  }

  if (numericDuration < 1) {
    return { isValid: false, error: 'Visa duration must be at least 1 day' };
  }

  if (numericDuration > 365) {
    return { isValid: false, error: 'Visa duration cannot exceed 365 days' };
  }

  return { isValid: true, error: null };
};

export const getTouristVisaSuggestions = () => {
  return [
    { 
      days: 30, 
      label: '30 days - Short visit', 
      common: true, 
      recommended: true,
      popular: true,
      description: 'Perfect for short trips and vacations'
    },
    { 
      days: 60, 
      label: '60 days - Extended visit', 
      common: true, 
      recommended: false,
      popular: true,
      description: 'Great for longer vacations'
    },
    { 
      days: 90, 
      label: '90 days - Tourist visa', 
      common: true, 
      recommended: true,
      popular: true,
      description: 'Most common tourist visa duration'
    },
    { 
      days: 180, 
      label: '180 days - Long-term tourist', 
      common: true, 
      recommended: false,
      popular: false,
      description: 'For extended stays and exploration'
    },
    { 
      days: 365, 
      label: '365 days - Annual visa', 
      common: false, 
      recommended: false,
      popular: false,
      description: 'Maximum duration for annual visas'
    }
  ];
};

export const formatDateForTourists = (date) => {
  // Import the universal formatting function
  const { formatDate } = require('./dateUtils');
  return formatDate(date);
};

export const getDaysUntilExpiry = (expiryDate) => {
  const now = new Date();
  const timeDiff = expiryDate - now;
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  return daysDiff;
};

export const getTouristAdvice = (daysUntilExpiry) => {
  if (daysUntilExpiry < 0) {
    return {
      status: 'expired',
      message: 'Your visa has expired. Please contact immigration authorities.',
      urgent: true,
      color: '#FF4C4C'
    };
  } else if (daysUntilExpiry <= 7) {
    return {
      status: 'urgent',
      message: 'Your visa expires very soon! Consider extending or departing.',
      urgent: true,
      color: '#FF6B35'
    };
  } else if (daysUntilExpiry <= 30) {
    return {
      status: 'warning',
      message: 'Your visa expires soon. Plan your departure or extension.',
      urgent: false,
      color: '#FFA500'
    };
  } else {
    return {
      status: 'valid',
      message: 'Your visa is valid. Enjoy your stay!',
      urgent: false,
      color: '#00FF85'
    };
  }
};

